'use client';

import { use, useMemo, useState } from 'react';
import {
    getSchoolName,
    SchoolUnit,
    validUnits
} from '@/lib/data';
import { globalAccounts } from '@/lib/data/global-accounts';
import { Account } from '@/lib/data/types';
import {
    Building2,
    Search,
    ChevronDown,
    ChevronRight,
    Eye,
    ShieldCheck,
    CreditCard,
    Info,
    X,
    PencilLine,
    Wallet
} from 'lucide-react';
import Link from 'next/link';
import { notFound } from 'next/navigation';

export default function BankAccountPage({ params }: { params: Promise<{ unit: string }> }) {
    const { unit: initialUnit } = use(params);

    if (!validUnits.includes(initialUnit as SchoolUnit)) {
        notFound();
    }

    const baseType = initialUnit.split('-')[0];
    const sisterUnits = validUnits.filter(u =>
        u.startsWith(baseType) &&
        (u.includes('-') || !validUnits.some(v => v.startsWith(baseType) && v.includes('-')))
    );

    const defaultUnit = initialUnit.includes('-')
        ? initialUnit as SchoolUnit
        : (sisterUnits.find(u => u.includes('-')) || initialUnit) as SchoolUnit;

    const [activeUnit, setActiveUnit] = useState<SchoolUnit>(defaultUnit);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedBank, setSelectedBank] = useState<Account | null>(null);

    const schoolName = getSchoolName(activeUnit);

    // Get bank accounts from global accounts (1102-xxx)
    const bankAccounts = useMemo(() => {
        return globalAccounts.filter(acc =>
            acc.code.startsWith('1102-') && acc.parentId === 'ACC-1102'
        );
    }, []);

    const filteredAccounts = useMemo(() => {
        return bankAccounts.filter(a =>
            a.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            a.code.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [bankAccounts, searchTerm]);

    // Calculate total balance
    const totalBalance = useMemo(() => {
        const total = bankAccounts.reduce((sum, acc) => {
            const balance = parseInt(acc.balance.replace(/[^\d]/g, ''));
            return sum + balance;
        }, 0);
        return `Rp ${total.toLocaleString('id-ID')}`;
    }, [bankAccounts]);

    return (
        <main className="p-4 md:p-10 space-y-8 max-w-7xl mx-auto w-full pb-32 animate-in fade-in duration-700">
            {/* Header Area */}
            <section className="bg-surface p-8 rounded-4xl border border-border-main shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 w-96 h-96 bg-primary-soft/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>

                <div className="relative z-10 space-y-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                        <div className="space-y-4">
                            <div className="flex items-center gap-2 text-[10px] font-bold text-muted uppercase tracking-widest">
                                <Link href={`/yayasan/${initialUnit}/keuangan`} className="hover:text-primary transition-all">Keuangan</Link>
                                <ChevronRight className="w-3.5 h-3.5" />
                                <span className="text-foreground">Rekening Bank</span>
                            </div>

                            <div className="flex items-center gap-6">
                                <div className="w-16 h-16 rounded-2xl bg-primary-soft/50 border border-primary/10 flex items-center justify-center text-primary shadow-inner">
                                    <CreditCard className="w-8 h-8" />
                                </div>
                                <div className="space-y-1">
                                    <h1 className="text-3xl md:text-4xl font-bold text-foreground tracking-tight whitespace-nowrap">Rekening Bank</h1>
                                    <p className="text-secondary font-medium flex items-center gap-2">
                                        <Building2 className="w-3.5 h-3.5 text-primary" /> Kelola Rekening Unit {baseType.toUpperCase()}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Unit Selector */}
                        <div className="relative min-w-[280px]">
                            <label className="text-[10px] font-bold text-muted uppercase tracking-widest mb-2 block ml-1">Pilih Unit Sekolah</label>
                            <div className="relative group">
                                <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-primary group-hover:scale-110 transition-transform" />
                                <select
                                    className="w-full pl-11 pr-10 py-3.5 bg-background border border-border-main rounded-2xl text-sm font-bold focus:ring-4 focus:ring-primary/10 focus:border-primary transition-all appearance-none cursor-pointer outline-none shadow-sm"
                                    value={activeUnit}
                                    onChange={(e) => setActiveUnit(e.target.value as SchoolUnit)}
                                >
                                    {sisterUnits.map(u => (
                                        <option key={u} value={u}>{getSchoolName(u as SchoolUnit)}</option>
                                    ))}
                                </select>
                                <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted pointer-events-none" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Quick Summary Cards */}
            <section className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                    { label: 'Total Saldo Bank', value: totalBalance, icon: Wallet, color: 'text-primary' },
                    { label: 'Jumlah Rekening', value: bankAccounts.length.toString(), icon: CreditCard, color: 'text-info' },
                    { label: 'Status Audit', value: 'Verified', icon: ShieldCheck, color: 'text-success' },
                ].map((stat, i) => (
                    <div key={i} className="bg-surface p-6 rounded-3xl border border-border-main shadow-sm flex items-center gap-5">
                        <div className="w-12 h-12 bg-background border border-border-main rounded-2xl flex items-center justify-center">
                            <stat.icon className={`w-6 h-6 ${stat.color}`} />
                        </div>
                        <div>
                            <p className="text-[10px] font-bold text-muted uppercase tracking-widest">{stat.label}</p>
                            <p className="text-xl font-bold text-foreground">{stat.value}</p>
                        </div>
                    </div>
                ))}
            </section>

            {/* Bank Accounts Table */}
            <section className="bg-surface rounded-4xl border border-border-main shadow-sm overflow-hidden flex flex-col min-h-[400px]">
                <div className="p-6 border-b border-border-main flex flex-col md:flex-row md:items-center justify-between gap-4 bg-background/30">
                    <div className="relative flex-1 max-w-md">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
                        <input
                            type="text"
                            placeholder="Cari nama bank atau pemilik..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-11 pr-4 py-3 bg-background border border-border-main rounded-2xl text-sm focus:ring-4 focus:ring-primary/10 focus:border-primary transition-all outline-none"
                        />
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="bg-background/50 border-b border-border-main">
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest">Nama Bank</th>
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest">Nomor Rekening</th>
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest">Nama Pemilik</th>
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest text-right">Saldo</th>
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest text-center">Status</th>
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest text-right">Aksi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border-main">
                            {filteredAccounts.length > 0 ? filteredAccounts.map((bank: Account) => (
                                <tr key={bank.id} className="hover:bg-primary-soft/10 transition-colors group">
                                    <td className="px-8 py-5 whitespace-nowrap">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-xl bg-background border border-border-main flex items-center justify-center font-bold text-primary group-hover:scale-110 transition-transform">
                                                {bank.name[0]}
                                            </div>
                                            <span className="font-bold text-foreground">{bank.name}</span>
                                        </div>
                                    </td>
                                    <td className="px-8 py-5 whitespace-nowrap font-medium text-secondary tabular-nums">
                                        {bank.code}
                                    </td>
                                    <td className="px-8 py-5 whitespace-nowrap font-bold text-foreground">
                                        Yayasan Pendidikan
                                    </td>
                                    <td className="px-8 py-5 text-right whitespace-nowrap tabular-nums font-bold text-primary">
                                        {bank.balance}
                                    </td>
                                    <td className="px-8 py-5 text-center">
                                        <span className={`px-2.5 py-1 rounded-full text-[9px] font-bold uppercase tracking-wider ${bank.status === 'active' ? 'bg-success-soft text-success' : 'bg-error-soft text-error'
                                            }`}>
                                            {bank.status === 'active' ? 'Aktif' : 'Non-Aktif'}
                                        </span>
                                    </td>
                                    <td className="px-8 py-5 text-right whitespace-nowrap">
                                        <div className="flex items-center justify-end gap-2">
                                            <button
                                                onClick={() => setSelectedBank(bank)}
                                                className="p-2 rounded-xl bg-background border border-border-main text-muted hover:text-primary hover:border-primary transition-all"
                                                title="Lihat Detail"
                                            >
                                                <Eye className="w-4 h-4" />
                                            </button>

                                        </div>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan={6} className="px-8 py-32 text-center text-muted">
                                        <div className="flex flex-col items-center gap-4 opacity-30">
                                            <CreditCard className="w-16 h-16" />
                                            <p className="font-bold text-sm uppercase tracking-widest">Tidak ada rekening ditemukan</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </section>

            {/* Detail Modal */}
            {selectedBank && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity" onClick={() => setSelectedBank(null)}></div>
                    <div className="relative bg-surface w-full max-w-lg rounded-4xl border border-border-main shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
                        <div className="p-8 border-b border-border-main flex items-center justify-between bg-background/50">
                            <div className="flex items-center gap-5">
                                <div className="w-14 h-14 rounded-2xl bg-primary-soft/50 border border-primary/10 flex items-center justify-center text-primary">
                                    <CreditCard className="w-7 h-7" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-foreground">Detail Rekening</h3>
                                    <p className="text-secondary text-xs font-medium uppercase tracking-widest mt-0.5">{selectedBank.name}</p>
                                </div>
                            </div>
                            <button onClick={() => setSelectedBank(null)} className="w-10 h-10 rounded-xl bg-background border border-border-main flex items-center justify-center text-muted hover:text-foreground hover:rotate-90 transition-all duration-300">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-8 space-y-6">
                            <div className="grid grid-cols-2 gap-6">
                                <div className="space-y-1.5">
                                    <p className="text-[10px] font-bold text-muted uppercase tracking-widest">Kode Akun</p>
                                    <p className="text-sm font-bold text-foreground tabular-nums">{selectedBank.code}</p>
                                </div>
                                <div className="space-y-1.5">
                                    <p className="text-[10px] font-bold text-muted uppercase tracking-widest">Nama Pemilik</p>
                                    <p className="text-sm font-bold text-foreground">Yayasan Pendidikan</p>
                                </div>
                                <div className="space-y-1.5">
                                    <p className="text-[10px] font-bold text-muted uppercase tracking-widest">Kategori</p>
                                    <p className="text-sm font-bold text-foreground">{selectedBank.subCategory}</p>
                                </div>
                                <div className="space-y-1.5">
                                    <p className="text-[10px] font-bold text-muted uppercase tracking-widest">Tipe Saldo</p>
                                    <p className="text-sm font-bold text-primary uppercase">{selectedBank.balanceType}</p>
                                </div>
                            </div>

                            <div className="p-6 bg-background rounded-3xl border border-border-main flex items-center justify-between">
                                <div>
                                    <p className="text-[10px] font-bold text-muted uppercase tracking-widest mb-1">Saldo Saat Ini</p>
                                    <p className="text-2xl font-bold text-foreground tabular-nums tracking-tight">{selectedBank.balance}</p>
                                </div>
                                <div className={`px-4 py-2 rounded-xl text-[10px] font-bold uppercase tracking-widest border ${selectedBank.status === 'active' ? 'bg-success-soft text-success border-success/10' : 'bg-error-soft text-error border-error/10'
                                    }`}>
                                    {selectedBank.status === 'active' ? 'Terverifikasi' : 'Tertangguh'}
                                </div>
                            </div>

                            <div className="flex gap-4 p-5 bg-info-soft rounded-2xl border border-info/10 text-info">
                                <Info className="w-5 h-5 shrink-0" />
                                <div className="space-y-1">
                                    <p className="text-[10px] font-bold uppercase tracking-widest">Catatan</p>
                                    <p className="text-xs font-medium leading-relaxed opacity-90">Rekening ini digunakan untuk penerimaan SPP dan biaya operasional unit {schoolName}.</p>
                                </div>
                            </div>
                        </div>

                        <div className="p-8 bg-background/50 border-t border-border-main flex justify-end">
                            <button onClick={() => setSelectedBank(null)} className="px-8 py-3.5 bg-surface border border-border-main text-secondary rounded-2xl text-[10px] font-bold uppercase tracking-widest hover:bg-background transition-all">Tutup</button>
                        </div>
                    </div>
                </div>
            )}
        </main>
    );
}
